import { isElementNode, isHTMLElementNode } from "./isNode";

/**
 * Check if the given node is irrelevant in terms of text selection.
 *
 * Nodes are considered irrelevant if they're either hidden or not selectable
 * (in which case the user cannot highlight them in the browser, so it doesn't
 * make sense to read them out loud either).
 */
export function isIrrelevantNode(node: Node): boolean {
  if (isElementNode(node)) {
    // element nodes are considered irrelevant only if they're completely hidden
    // (as this setting cannot be altered by nested nodes anyway)
    const elementNodeStyle = window.getComputedStyle(node);

    // In case an element explicitly requests to disable polly, then consider
    // it irrelevant no matter what.
    if (isHTMLElementNode(node) && node.dataset.disablePolly) {
      return true;
    }

    return (
      elementNodeStyle.display === "none" ||
      elementNodeStyle.visibility === "hidden" ||
      (isHTMLElementNode(node) && node.classList.contains("d-sr-only"))
    );
  }

  if (!node.parentElement) {
    // root-level text nodes are always selectable as no custom styling could've
    // been applied to them
    return false;
  }

  // all other text nodes are considered irrelevant if they cannot be selected
  // in any way
  const textNodeStyle = window.getComputedStyle(node.parentElement);

  return (
    textNodeStyle.userSelect === "none" ||
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: msUserSelect IS valid in legacy IE browsers
    textNodeStyle.msUserSelect === "none" ||
    textNodeStyle.pointerEvents === "none"
  );
}
